import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../../../App.css";
import searchIndex, { isPublicSearchPath } from "../../../data/searchIndex";
import { useAuth } from "../../../context/AuthContext";
import {
  fetchJsonApi,
  logBlockedSearchPayload,
  stripSearchableHtml,
  validateSearchQuery,
} from "../../../utils/searchSecurity";

const RESULT_LIMIT = 50;

function normalizeForSearch(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9_+\-.'\s/]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function splitTerms(query) {
  return normalizeForSearch(query)
    .split(" ")
    .map((term) => term.trim())
    .filter((term) => term.length > 1);
}

function makeSnippet(text, terms) {
  const plain = stripSearchableHtml(text);

  if (!plain) {
    return "Public Pogforts result.";
  }

  const lowerPlain = plain.toLowerCase();
  const firstHit = terms
    .map((term) => lowerPlain.indexOf(term.toLowerCase()))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b)[0];

  if (firstHit === undefined) {
    return plain.length > 220 ? `${plain.slice(0, 217)}...` : plain;
  }

  const start = Math.max(0, firstHit - 80);
  const end = Math.min(plain.length, firstHit + 180);
  const prefix = start > 0 ? "..." : "";
  const suffix = end < plain.length ? "..." : "";

  return `${prefix}${plain.slice(start, end).trim()}${suffix}`;
}

function scoreEntry(entry, query, terms) {
  const normalizedQuery = normalizeForSearch(query);
  const title = normalizeForSearch(entry.title);
  const path = normalizeForSearch(entry.path);
  const category = normalizeForSearch(entry.category);
  const keywords = normalizeForSearch((entry.keywords || []).join(" "));
  const content = normalizeForSearch(`${entry.excerpt || ""} ${entry.content || ""}`);

  let score = 0;

  if (title === normalizedQuery) score += 140;
  if (title.includes(normalizedQuery)) score += 80;
  if (keywords.includes(normalizedQuery)) score += 55;
  if (path.includes(normalizedQuery)) score += 40;
  if (category.includes(normalizedQuery)) score += 30;
  if (content.includes(normalizedQuery)) score += 20;

  for (const term of terms) {
    if (title.includes(term)) score += 24;
    if (keywords.includes(term)) score += 18;
    if (path.includes(term)) score += 14;
    if (category.includes(term)) score += 10;
    if (content.includes(term)) score += 6;
  }

  if (entry.type === "blog-post") score += 3;

  return score;
}

function sortResults(a, b) {
  if (b.score !== a.score) return b.score - a.score;

  const dateA = a.entry.date || a.entry.createdAt || "";
  const dateB = b.entry.date || b.entry.createdAt || "";

  if (dateA || dateB) {
    return String(dateB).localeCompare(String(dateA));
  }

  return String(a.entry.title).localeCompare(String(b.entry.title));
}

function buildBlogSearchEntries(posts) {
  return (posts || [])
    .filter((post) => post?.slug && isPublicSearchPath(`/blog/${post.slug}`))
    .map((post) => {
      const plainBody = stripSearchableHtml(post.body || post.excerpt || "");
      const plainExcerpt = stripSearchableHtml(post.excerpt || plainBody);

      return {
        id: `blog-${post.slug}`,
        type: "blog-post",
        category: post.category || "Blog",
        title: post.title || "Untitled Blog Post",
        path: `/blog/${post.slug}`,
        keywords: [
          "blog",
          "post",
          post.author || "",
          post.category || "",
          post.title || "",
        ],
        excerpt: plainExcerpt,
        content: plainBody,
        date: post.date || null,
        createdAt: post.createdAt || null,
        author: post.author || null,
      };
    });
}

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const rawQuery = searchParams.get("q") || "";
  const validation = useMemo(() => validateSearchQuery(rawQuery), [rawQuery]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(false);
  const [blogError, setBlogError] = useState("");

  useEffect(() => {
    if (validation.reason !== "dangerous_payload") return;

    logBlockedSearchPayload({
      query: rawQuery,
      reason: validation.reason,
      userId: user?.id || "guest",
      source: "search_results_querystring",
    });
  }, [rawQuery, user, validation.reason]);

  useEffect(() => {
    let cancelled = false;

    if (!validation.ok) {
      setBlogPosts([]);
      setBlogError("");
      return () => {
        cancelled = true;
      };
    }

    setLoadingBlogs(true);
    setBlogError("");

    fetchJsonApi("/api/blog/posts")
      .then((posts) => {
        if (!cancelled) {
          setBlogPosts(Array.isArray(posts) ? posts : []);
        }
      })
      .catch((error) => {
        console.warn("Blog search index unavailable:", error);

        if (!cancelled) {
          setBlogPosts([]);
          setBlogError("Blog posts could not be loaded for this search.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingBlogs(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [validation.ok, validation.value]);

  const results = useMemo(() => {
    if (!validation.ok) return [];

    const terms = splitTerms(validation.value);
    const blogEntries = buildBlogSearchEntries(blogPosts);
    const combinedEntries = [...searchIndex, ...blogEntries].filter((entry) =>
      isPublicSearchPath(entry.path)
    );

    return combinedEntries
      .map((entry) => ({
        entry,
        score: scoreEntry(entry, validation.value, terms),
      }))
      .filter((result) => result.score > 0)
      .sort(sortResults)
      .slice(0, RESULT_LIMIT)
      .map((result) => ({
        ...result,
        snippet: makeSnippet(
          `${result.entry.excerpt || ""} ${result.entry.content || ""}`,
          terms
        ),
      }));
  }, [blogPosts, validation.ok, validation.value]);

  const titleQuery = validation.ok ? validation.value : rawQuery;

  return (
    <div className="profile-shell search-results-shell">
      <Helmet>
        <title>{titleQuery ? `${titleQuery} - Pogforts Search` : "Pogforts Search"}</title>
        <meta
          name="description"
          content="Search Pogforts public pages, blog posts, news, legal content, and special public pages."
        />
      </Helmet>

      <div className="profile-panel dossier-panel search-results-panel">
        <div className="profile-topbar search-results-topbar">
          <div>
            <div className="profile-kicker search-results-kicker">POGFORTS SEARCH</div>
            <h1 className="profile-title search-results-title">Search Results</h1>
          </div>

          <div className="profile-top-actions search-results-actions">
            <Link to="/" className="profile-admin-button search-results-home-link">
              Home
            </Link>
          </div>
        </div>

        {!validation.ok && validation.reason === "empty" && (
          <div className="profile-identity-card search-results-message">
            Enter a search query from the home page search box.
          </div>
        )}

        {!validation.ok && validation.reason !== "empty" && (
          <div className="profile-briefcase-alert search-results-message search-results-alert" role="alert">
            {validation.message}
          </div>
        )}

        {validation.ok && (
          <>
            <div className="profile-identity-card search-results-query-line">
              <span className="profile-identity-label">Query</span>
              <span className="profile-identity-value">{validation.value}</span>
            </div>

            {blogError && (
              <div className="profile-briefcase-alert search-results-message search-results-warning" role="status">
                {blogError}
              </div>
            )}

            {loadingBlogs && (
              <div className="profile-identity-card search-results-message" role="status">
                Loading blog index...
              </div>
            )}

            {!loadingBlogs && results.length === 0 && (
              <div className="profile-identity-card search-results-message">
                <span className="profile-identity-label">No Records</span>
                <span className="profile-identity-value">
                  No public Pogforts results found for <strong>{validation.value}</strong>.
                </span>
              </div>
            )}

            <div className="search-results-list" aria-label="Pogforts search results">
              {results.map(({ entry, snippet }) => (
                <article className="profile-identity-card search-result-item" key={entry.id}>
                  <div className="search-result-header-row">
                    <Link to={entry.path} className="search-result-title">
                      {entry.title}
                    </Link>
                    <span className="search-result-type">{entry.category}</span>
                  </div>
                  <div className="search-result-path">{entry.path}</div>
                  <div className="search-result-meta">
                    {entry.author ? `${entry.author}` : "Pogforts"}
                    {entry.date ? ` • ${entry.date}` : ""}
                  </div>
                  <p className="search-result-snippet">{snippet}</p>
                </article>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
